/**
 * Free plan rules, in one place.
 *
 * The server decides what is allowed; this file decides what the UI shows. The
 * two lists have to agree, so when a feature moves between plans it moves here
 * and in the API's matching table, in the same release.
 */

export type FeatureKey =
  | "birth_certificate"
  | "first_year"
  | "in_motion"
  | "family_tree"
  | "month_by_month"
  | "first_birthday"
  | "teeth_chart"
  | "first_holiday"
  | "first_words"
  | "posts";

/** Everything a family gets without paying. */
export const FREE_FEATURES: FeatureKey[] = [
  "birth_certificate",
  "first_year",
  "in_motion",
];

/** How many people can be on one baby without paying: the owner and two invited. */
export const FREE_SEATS = 3;

/** Route path -> feature. Routes not listed here are not plan-gated at all. */
export const FEATURE_BY_PATH: Record<string, FeatureKey> = {
  "/baby-info": "birth_certificate",
  "/first-year": "first_year",
  "/in-motion": "in_motion",
  "/family-tree": "family_tree",
  "/monthly-diary": "month_by_month",
  "/first-birthday": "first_birthday",
  "/teeth-chart": "teeth_chart",
  "/first-holiday": "first_holiday",
  "/first-words": "first_words",
};

/** The name to use when telling someone what they are missing. */
export const FEATURE_LABELS: Record<FeatureKey, string> = {
  birth_certificate: "Birth Certificate",
  first_year: "My first year",
  in_motion: "We are in motion",
  family_tree: "Family Tree",
  month_by_month: "Month by month",
  first_birthday: "My First Birthday",
  teeth_chart: "Teeth Chart",
  first_holiday: "My first holiday",
  first_words: "First Words",
  posts: "Adding photos",
};

/**
 * Raised when the server refuses a write because the baby is on the free plan.
 * One listener turns it into a notice, so no page has to handle a 402 itself.
 */
export const PRO_REQUIRED_EVENT = "littlefeather:pro-required";

export const announceProRequired = (feature?: FeatureKey) =>
  window.dispatchEvent(
    new CustomEvent(PRO_REQUIRED_EVENT, { detail: { feature } })
  );

export const isFreeFeature = (feature: FeatureKey): boolean =>
  FREE_FEATURES.includes(feature);

/**
 * Whether a subscriber-only feature is available.
 *
 * `hasActivePayment` is the single entitlement flag the app already carries. A
 * paying family gets everything; a free one gets the three features above.
 */
/**
 * Whether a feature is available on the baby currently being viewed.
 *
 * `babyHasActivePayment` is the BABY's entitlement, not the caller's: the
 * parent buys the journal and invited family contribute to it. Passing the
 * caller's own flag here is the bug that showed grandparents locks over a baby
 * somebody else was paying for.
 *
 * `null` means the answer has not arrived. Treat it as unlocked — a moment of
 * open UI that then locks is far better than flashing locks at a family who
 * has paid, and the server refuses the write either way.
 */
export const canUseFeature = (
  feature: FeatureKey,
  babyHasActivePayment: boolean | null
): boolean =>
  babyHasActivePayment === null ||
  babyHasActivePayment ||
  isFreeFeature(feature);

export const featureForPath = (path: string): FeatureKey | undefined =>
  FEATURE_BY_PATH[path];
